import { createSlice } from '@reduxjs/toolkit';
import {
  ListName,
  PaginationAndSortState,
  ResetListStateByNameAction,
  SetCurrentPageAction,
  SetCurrentPageSizeAction,
  SetSortByAction,
} from './paginationAndSort.types';
import { createSelector, createSliceHook } from '../helpers';
import { Pagination } from '@dynamika-shared/ui/Table/Table.types';

const initialState: PaginationAndSortState = {
  lists: {
    clinicalTrials: {
      currentPageSize: 10,
      currentPage: 1,
    },
    cohorts: {
      currentPageSize: 10,
      currentPage: 1,
    },
    sites: {
      currentPageSize: 20,
      currentPage: 1,
    },
    subjectList: {
      currentPageSize: 10,
      currentPage: 1,
    },
    externalUserSubjects: {
      currentPageSize: 10,
      currentPage: 1,
    },
    validationSpecialistSubjects: {
      currentPageSize: 10,
      currentPage: 1,
    },
    externalUserReadingCompleted: {
      currentPageSize: 10,
      currentPage: 1,
    },
    QCList: {
      currentPageSize: 10,
      currentPage: 1,
    },
    teamMembers: {
      currentPageSize: 20,
      currentPage: 1,
    },
    users: {
      currentPageSize: 20,
      currentPage: 1,
    },
    auditTrials: {
      currentPageSize: 10,
      currentPage: 1,
    },
    readings: {
      currentPageSize: 10,
      currentPage: 1,
    },
    reports: {
      currentPageSize: 10,
      currentPage: 1,
    },
    scoringForms: {
      currentPageSize: 10,
      currentPage: 1,
      sortBy: 'code,asc',
    },
    tags: {
      currentPageSize: 10,
      currentPage: 1,
      sortBy: 'code,asc',
    },
    functions: {
      currentPageSize: 10,
      currentPage: 1,
      sortBy: 'code,asc',
    },
    measurements: {
      currentPageSize: 10,
      currentPage: 1,
      sortBy: 'code,asc',
    },
    readers: {
      currentPageSize: 10,
      currentPage: 1,
      sortBy: 'code,asc',
    },
    instruments: {
      currentPageSize: 10,
      currentPage: 1,
      sortBy: 'code,asc',
    },
    reportTemplates: {
      currentPageSize: 10,
      currentPage: 1,
      sortBy: 'code,asc',
    },
    dictionaries: {
      currentPageSize: 10,
      currentPage: 1,
      sortBy: 'code,asc',
    },
    readingCompleted: {
      currentPageSize: 10,
      currentPage: 1,
    },
    dashboardOverview: {
      currentPageSize: 10,
      currentPage: 1,
      sortBy: 'uploadDate,desc',
    },
    qcQueries: {
      currentPageSize: 10,
      currentPage: 1,
    },
    siteUserQueries: {
      currentPageSize: 10,
      currentPage: 1,
    },
    queries: {
      currentPageSize: 10,
      currentPage: 1,
    },
    overviewUploads: {
      currentPageSize: 10,
      currentPage: 1,
    },
    subscriptions: {
      currentPageSize: 10,
      currentPage: 1,
    },
    notificationHistory: {
      currentPageSize: 10,
      currentPage: 1,
      sortBy: 'dispatchedAt,desc',
    },
    notificationSiteRecipients: {
      currentPageSize: 20,
      currentPage: 1,
      sortBy: 'fullName,asc',
    },
  },
};

export const paginationAndSortSlice = createSlice({
  name: 'paginationAndSort',
  initialState,
  reducers: {
    setCurrentPageSize: (state, action: SetCurrentPageSizeAction) => {
      const { listName, newPageSize, newPage } = action.payload;
      state.lists[listName].currentPageSize = newPageSize;
      if (newPage) {
        state.lists[listName].currentPage = newPage;
      }
    },
    setCurrentPage: (state, action: SetCurrentPageAction) => {
      const { listName, newPage, newPageSize } = action.payload;
      state.lists[listName].currentPage = newPage;
      if (newPageSize) {
        state.lists[listName].currentPageSize = newPageSize;
      }
    },
    setSortBy: (state, action: SetSortByAction) => {
      const { listName, newSortBy } = action.payload;
      state.lists[listName].sortBy = newSortBy;
    },
    resetListStateByName: (state, action: ResetListStateByNameAction) => {
      const { listName } = action.payload;
      state.lists[listName] = initialState.lists[listName];
    },
  },
});

export const {
  setCurrentPageSize,
  setCurrentPage,
  setSortBy,
  resetListStateByName,
} = paginationAndSortSlice.actions;

export const actions = {
  ...paginationAndSortSlice.actions,
};

export const usePaginationAndSortSlice = createSliceHook(
  paginationAndSortSlice.name,
  actions
);

export const selectPaginationAndSort = createSelector('paginationAndSort');

/**
 * Hook to create a pagination object for a table list
 * @param  {ListName}   tableName - needed to save pagination in redux for some table
 * @param  {boolean}    hasData - to enable pagination
 * @param  {Function}  onPaginationChange handler to change page and size
 * @return {Pagination}    object of pagination
 */
export function useMakePagination(
  tableName: ListName,
  hasData: boolean,
  onPaginationChange?: (page: number, pageSize: number) => void
): Pagination {
  const [pagination, actions] = usePaginationAndSortSlice((state, actions) => [
    state.lists[tableName],
    actions,
  ]);
  return {
    currentPage: pagination.currentPage,
    pageSize: pagination.currentPageSize,
    tableName,
    enabled: hasData,
    setCurrentPage: (page, pageSize) => {
      onPaginationChange?.(page, pageSize ?? pagination.currentPageSize);
      return actions.setCurrentPage({
        listName: tableName,
        newPage: page,
        newPageSize: pageSize,
      });
    },
    setPageSize: (pageSize, page) => {
      onPaginationChange?.(page ?? pagination.currentPage, pageSize);
      return actions.setCurrentPageSize({
        listName: tableName,
        newPageSize: pageSize,
      });
    },
  };
}
